import React, { useEffect, useState } from "react";
import { Loader2, Crown, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { memberApi, errMsg } from "../lib/api";
import { FAQS } from "../mock";
import { Page } from "./ContentViews";
import RazorpayButton from "../components/RazorpayButton";

const d = (iso) => (iso ? new Date(iso).toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" }) : "-");

export function ProfileView({ user, setUser }) {
  const [f, setF] = useState({ name: user.name || "", nickname: user.nickname || "", about: user.about || "" });
  const [pw, setPw] = useState({ current: "", new: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const { data } = await memberApi.patch("/member/me", f); setUser(data); toast.success("Profile saved"); }
    catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try { await memberApi.post("/member/password", pw); setPw({ current: "", new: "" }); toast.success("Password updated"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Page title="My Profile" sub="Help Maya get to know you">
      <form onSubmit={save} className="mem-card p-6 space-y-4 max-w-[620px]" data-testid="profile-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input className="mt-1.5" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name" /></div>
          <div><Label>What should Maya call you?</Label><Input className="mt-1.5" placeholder="Nickname" value={f.nickname} onChange={(e) => setF({ ...f, nickname: e.target.value })} data-testid="profile-nickname" /></div>
        </div>
        <div><Label>Email</Label><Input className="mt-1.5" value={user.email} disabled /></div>
        <div><Label>About you</Label><Textarea className="mt-1.5" rows={4} maxLength={600} placeholder="Your hobbies, work, favourite food... Maya will remember." value={f.about} onChange={(e) => setF({ ...f, about: e.target.value })} data-testid="profile-about" /></div>
        <button className="mem-btn" disabled={saving} data-testid="profile-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save profile"}</button>
      </form>
      <form onSubmit={changePw} className="mem-card p-6 space-y-4 max-w-[620px] mt-5">
        <h3 className="text-[17px] font-semibold">Change password</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input type="password" placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" />
          <Input type="password" placeholder="New password (6+)" value={pw.new} onChange={(e) => setPw({ ...pw, new: e.target.value })} data-testid="pw-new" />
        </div>
        <button className="mem-btn-outline" data-testid="pw-save">Update password</button>
      </form>
    </Page>
  );
}

export function SubscriptionView({ user, setUser, reload }) {
  const s = user.subscription || {};
  const [busy, setBusy] = useState(false);
  const act = async (path, msg) => {
    setBusy(true);
    try { await memberApi.post(`/member/subscription/${path}`); await reload(); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  // Poll while awaiting a Razorpay payment, so VIP unlocks automatically once the webhook confirms it.
  useEffect(() => {
    if (user.active) return;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [user.active]); // eslint-disable-line

  const statusLabel = s.status === "active" ? "Active" : user.active ? "Cancelled – access until end of period" : s.status === "pending" ? "Payment pending" : "Expired";
  return (
    <Page title="Manage Subscription">
      <div className="mem-card p-6 max-w-[620px]" data-testid="sub-card">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-[#fde3e9] grid place-items-center text-[#e8365f]"><Crown className="w-6 h-6" /></span>
          <div><p className="text-[19px] font-semibold">{s.plan || "Maya VIP"}</p><p className="text-[14px] text-[#6b5d60]">₹{s.price || 499}/month</p></div>
          <span className={`ml-auto text-[12px] font-semibold px-3 py-1 rounded-full ${s.status === "active" ? "bg-[#e3f5ea] text-[#227a47]" : "bg-[#fdeaea] text-[#b42318]"}`} data-testid="sub-status">{statusLabel}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-6 text-[14px]">
          <div className="mem-soft p-4"><CalendarDays className="w-4 h-4 text-[#e8365f]" /><p className="text-[#9a8b8e] mt-2 text-[12px]">Member since</p><p className="font-medium">{d(s.started_at || user.created_at)}</p></div>
          <div className="mem-soft p-4"><CalendarDays className="w-4 h-4 text-[#e8365f]" /><p className="text-[#9a8b8e] mt-2 text-[12px]">{s.status === "active" ? "Renews on" : "Access until"}</p><p className="font-medium">{d(s.renews_at)}</p></div>
        </div>
        <div className="mt-6">
          {s.status === "active" ? (
            <AlertDialog>
              <AlertDialogTrigger asChild><button className="mem-btn-outline" disabled={busy} data-testid="sub-cancel">Cancel subscription</button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Cancel Maya VIP?</AlertDialogTitle><AlertDialogDescription>You'll keep access until {d(s.renews_at)}. You can resume anytime.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Keep VIP</AlertDialogCancel><AlertDialogAction onClick={() => act("cancel", "Subscription cancelled")} className="bg-[#e8365f] hover:bg-[#cf2650]" data-testid="sub-cancel-confirm">Cancel</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : user.active ? (
            <button className="mem-btn" disabled={busy} onClick={() => act("resume", "Welcome back!")} data-testid="sub-resume">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resume subscription"}</button>
          ) : (
            <div className="rounded-xl border border-[#f0e4e8] p-4 bg-[#fffafb]" data-testid="sub-pay">
              <p className="text-[13.5px] text-[#4a4246] mb-3">{s.status === "pending" ? "Complete your payment to unlock Maya VIP:" : "Renew to unlock chat, photos and videos again:"}</p>
              <div className="grid place-items-center"><RazorpayButton email={user.email} name={user.name} /></div>
              <div className="flex items-center justify-center gap-2 text-[12px] text-[#8a8286] mt-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> VIP unlocks automatically a few seconds after payment.</div>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export function SettingsView({ user, setUser }) {
  const toggle = async (v) => {
    try { const { data } = await memberApi.patch("/member/me", { notify_email: v }); setUser(data); toast.success("Saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Page title="Settings">
      <div className="mem-card p-6 max-w-[620px] flex items-center justify-between">
        <div><p className="font-medium">Email me when Maya posts</p><p className="text-[13px] text-[#6b5d60]">New updates, polls and videos</p></div>
        <Switch checked={user.notify_email !== false} onCheckedChange={toggle} className="data-[state=checked]:bg-[#e8365f]" data-testid="settings-notify" />
      </div>
    </Page>
  );
}

export function HelpView() {
  return (
    <Page title="Help & Support">
      <div className="mem-card p-6 max-w-[720px]">
        <Accordion type="single" collapsible>
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left hover:no-underline hover:text-[#e8365f]">{f.q}</AccordionTrigger><AccordionContent className="text-[#5a4c4f]">{f.a}</AccordionContent></AccordionItem>
          ))}
        </Accordion>
        <p className="text-[13px] text-[#6b5d60] mt-5">Still need help? Write to <span className="text-[#e8365f]">support@silkymaya.com</span></p>
      </div>
    </Page>
  );
}

export function InactiveView({ user, go }) {
  const pending = user.subscription?.status === "pending";
  return (
    <Page title={pending ? "Complete your payment" : "Your VIP has ended"} sub={pending ? "One step away from unlocking Maya VIP" : "Renew to unlock chat, photos and videos again"}>
      <div className="mem-card p-6 max-w-[520px]" data-testid="inactive-view">
        <p className="text-[15px] text-[#3a2d30]">{pending ? `Almost there, ${user.nickname || user.name}!` : `I miss you already, ${user.nickname || user.name}. Come back?`}</p>
        <button className="mem-btn mt-5" onClick={() => go("subscription")} data-testid="inactive-renew">{pending ? "Complete payment" : "Manage subscription"}</button>
      </div>
    </Page>
  );
}
